import React, {useState} from 'react'
import PropTypes from 'prop-types'
import {makeStyles} from '@mui/styles'
import {Spacing, Colors, Typography} from '../../assets/styles'
import {borderRadiusMedium} from '../../assets/styles/border'
import {getChangeStyle} from '../ResidentSentiment/utils'
import ToggleButtons from '../ToggleButtons'
import DataComingSoon from '../DataComingSoon'

const WINDOW_OPTIONS = [
  {label: '30 day', key: 'thirtyDay', changeDescription: 'from last month'},
  {label: '6 month', key: 'sixMonth', changeDescription: 'from last 6 months'},
]
const WINDOW_LABELS = WINDOW_OPTIONS.map(o => o.label)

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {?(string|number)} props.value
 * @param {?number} props.change
 * @param {string} props.changeDescription
 * @param {string} [props.changeSuffix]
 */
function SummaryStatCard({title, value, change, changeDescription, changeSuffix = ''}) {
  const classes = useStyles()
  const hasChange = typeof change === 'number' && !isNaN(change)
  const changeStyle = getChangeStyle(change)
  const showEmptyState = value === null || value === undefined || (typeof value === 'number' && isNaN(value))

  return (
    <div className={classes.card}>
      <div className={classes.cardTitle}>{title}</div>
      {showEmptyState ? (
        <DataComingSoon />
      ) : (
        <div className={classes.cardBody}>
          <div className={classes.value}>{value}</div>
          {hasChange && (
            <div className={classes.changeContainer}>
              <div className={classes.change} style={{color: changeStyle.color}}>
                {changeStyle.sign}
                {change}
                {changeSuffix}
              </div>
              <div>{changeDescription}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

SummaryStatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  change: PropTypes.number,
  changeDescription: PropTypes.string.isRequired,
  changeSuffix: PropTypes.string,
}

/**
 * Owns its own 30 day / 6 month switch, independent of the one on the NPS score card.
 * @param {Object} props
 * @param {{thirtyDay: ?Object, sixMonth: ?Object}} props.summaryByWindow
 */
function ExitSurveySummaryCards({summaryByWindow}) {
  const classes = useStyles()

  const [windowIndex, setWindowIndex] = useState(0)

  const activeWindow = WINDOW_OPTIONS[windowIndex]
  const summary = summaryByWindow[activeWindow.key] || {}
  const {totalResponses, totalResponsesChange, responseRate, responseRateChange} = summary

  return (
    <React.Fragment>
      <div className={classes.switcherRow}>
        <div className={classes.switcher}>
          <ToggleButtons buttonLabels={WINDOW_LABELS} selected={windowIndex} onChange={setWindowIndex} inverseColors />
        </div>
      </div>
      <div className={classes.cardRow}>
        <SummaryStatCard
          title={'Total responses'}
          value={totalResponses}
          change={totalResponsesChange}
          changeDescription={activeWindow.changeDescription}
        />
        <SummaryStatCard
          title={'Response rate'}
          value={typeof responseRate === 'number' && !isNaN(responseRate) ? `${responseRate}%` : responseRate}
          change={responseRateChange}
          changeDescription={activeWindow.changeDescription}
          changeSuffix={'%'}
        />
      </div>
    </React.Fragment>
  )
}

const useStyles = makeStyles({
  switcherRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: Spacing.spaceSmall,
  },
  switcher: {
    flexShrink: 0,
    width: 160,
  },
  cardRow: {
    display: 'flex',
    gap: Spacing.spaceMedium,
    marginBottom: Spacing.spaceMedium,
    '& > div': {
      flex: 1,
    },
  },
  card: {
    height: 140,
    borderRadius: borderRadiusMedium,
    border: `1px solid ${Colors.rxrGreyColor}`,
    backgroundColor: Colors.rxrWhiteColor,
    padding: Spacing.spaceMedium,
    display: 'flex',
    flexDirection: 'column',
  },
  cardTitle: {
    fontSize: Typography.fontSizeLarge,
    fontWeight: Typography.heavyFontWeight,
    color: Colors.rxrBlackColor,
  },
  cardBody: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    gap: Spacing.spaceLarge,
  },
  value: {
    fontSize: Typography.fontSizeExtraLarge,
    fontWeight: Typography.mediumFontWeight,
    color: Colors.rxrBlackColor,
  },
  changeContainer: {
    fontSize: Typography.fontSizeMedium,
    fontWeight: Typography.lightFontWeight,
    color: Colors.rxrDarkGreyColor,
  },
  change: {
    fontSize: Typography.fontSizeMedium,
  },
})

ExitSurveySummaryCards.propTypes = {
  summaryByWindow: PropTypes.shape({
    thirtyDay: PropTypes.object,
    sixMonth: PropTypes.object,
  }).isRequired,
}

export default ExitSurveySummaryCards
